import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  X, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Lock,
  Unlock,
  RotateCcw
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:5001/api'
  : '/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PermissionsModal = ({ user, onClose, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [useRoleDefaults, setUseRoleDefaults] = useState(true);
  const [hasCustomPermissions, setHasCustomPermissions] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    fetchPermissions();
  }, [user.id]);

  const fetchPermissions = async () => {
    try {
      setLoading(true);

      // Fetch available permissions and user's current permissions in parallel
      const [availableRes, userPermsRes] = await Promise.all([
        axios.get(`${API_URL}/users/permissions/available`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_URL}/users/${user.id}/permissions`, {
          headers: getAuthHeaders()
        })
      ]);

      const { permissions, categories: permCategories, roleDefaults: defaults } = availableRes.data.data;
      const { permissions: userPerms, hasCustomPermissions: hasCustom } = userPermsRes.data.data;

      setAvailablePermissions(permissions);
      setCategories(permCategories);
      setRoleDefaults(defaults);
      setUserPermissions(userPerms);
      setSelectedPermissions(userPerms);
      setHasCustomPermissions(hasCustom);
      setUseRoleDefaults(!hasCustom);

      // Expand first category by default
      if (permCategories.length > 0) {
        setExpandedCategories({ [permCategories[0].key]: true });
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      alert('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePermission = (permission) => {
    if (useRoleDefaults) return; // Can't change when using role defaults

    setSelectedPermissions(prev => {
      if (prev.includes(permission)) {
        return prev.filter(p => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleToggleCategory = (categoryKey) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryKey]: !prev[categoryKey]
    }));
  };

  const handleSelectAllInCategory = (category) => {
    if (useRoleDefaults) return;

    const categoryPerms = category.permissions;
    const allSelected = categoryPerms.every(p => selectedPermissions.includes(p));

    if (allSelected) {
      // Deselect all in category
      setSelectedPermissions(prev => prev.filter(p => !categoryPerms.includes(p)));
    } else {
      // Select all in category
      setSelectedPermissions(prev => {
        const newPerms = [...prev];
        categoryPerms.forEach(p => {
          if (!newPerms.includes(p)) {
            newPerms.push(p);
          }
        });
        return newPerms;
      });
    }
  };

  const handleUseRoleDefaults = () => {
    const newValue = !useRoleDefaults;
    setUseRoleDefaults(newValue);

    if (newValue) {
      // Reset to role defaults
      setSelectedPermissions(roleDefaults[user.role] || []);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      await axios.put(
        `${API_URL}/users/${user.id}/permissions`,
        {
          permissions: useRoleDefaults ? null : selectedPermissions,
          useRoleDefaults
        },
        { headers: getAuthHeaders() }
      );

      alert('Permissions updated successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating permissions:', error);
      alert(error.response?.data?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionStats = () => {
    const total = Object.keys(availablePermissions).length;
    const selected = selectedPermissions.length;
    const percentage = total > 0 ? Math.round((selected / total) * 100) : 0;
    return { total, selected, percentage };
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800 border-purple-300',
      editor: 'bg-blue-100 text-blue-800 border-blue-300',
      viewer: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[role] || colors.viewer;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="text-lg">Loading permissions...</span>
          </div>
        </div>
      </div>
    );
  }

  const stats = getPermissionStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Manage Permissions</h2>
                <p className="text-sm text-gray-600">Configure access permissions for {user.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* User Info */}
          <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className="font-medium text-gray-900">{user.name}</span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRoleBadgeColor(user.role)}`}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
              </div>
              <p className="text-sm text-gray-600">{user.email}</p>
            </div>

            {/* Permission Stats */}
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{stats.selected}/{stats.total}</div>
              <div className="text-xs text-gray-600">Permissions ({stats.percentage}%)</div>
            </div>
          </div>

          {/* Role Defaults Toggle */}
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                {useRoleDefaults ? (
                  <Lock className="w-5 h-5 text-amber-600" />
                ) : (
                  <Unlock className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-medium text-gray-900">Permission Mode</h4>
                    <p className="text-sm text-gray-600">
                      {useRoleDefaults 
                        ? `Using default permissions for ${user.role} role`
                        : 'Using custom permissions (role defaults overridden)'}
                    </p>
                  </div>
                  <button
                    onClick={handleUseRoleDefaults}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      useRoleDefaults
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-amber-600 text-white hover:bg-amber-700'
                    }`}
                  >
                    {useRoleDefaults ? (
                      <>
                        <Unlock className="w-4 h-4 inline mr-2" />
                        Customize Permissions
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4 inline mr-2" />
                        Reset to Role Defaults
                      </>
                    )}
                  </button>
                </div>
                {hasCustomPermissions && useRoleDefaults && (
                  <div className="text-xs text-amber-700 flex items-center space-x-1">
                    <AlertTriangle className="w-3 h-3" />
                    <span>This user currently has custom permissions that will be reset</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Permissions List */}
        <div className="flex-1 overflow-y-auto p-6">
          {useRoleDefaults ? (
            <div className="text-center py-12">
              <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Role Defaults Active</h3>
              <p className="text-gray-600 mb-4">
                This user is using the default permissions for the <strong>{user.role}</strong> role.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Click "Customize Permissions" above to override and set custom permissions.
              </p>
              
              {/* Show what permissions they have */}
              <div className="max-w-2xl mx-auto bg-gray-50 rounded-lg p-6">
                <h4 className="font-medium text-gray-900 mb-3">Current Permissions ({selectedPermissions.length})</h4>
                <div className="flex flex-wrap gap-2 justify-center">
                  {selectedPermissions.slice(0, 10).map(perm => (
                    <span key={perm} className="px-3 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700">
                      {availablePermissions[perm]}
                    </span>
                  ))}
                  {selectedPermissions.length > 10 && (
                    <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-700 font-medium">
                      +{selectedPermissions.length - 10} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {categories.map(category => {
                const isExpanded = expandedCategories[category.key];
                const categoryPerms = category.permissions;
                const selectedInCategory = categoryPerms.filter(p => selectedPermissions.includes(p)).length;
                const allSelected = selectedInCategory === categoryPerms.length;
                const someSelected = selectedInCategory > 0 && selectedInCategory < categoryPerms.length;

                return (
                  <div key={category.key} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Category Header */}
                    <div 
                      className="bg-gray-50 p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => handleToggleCategory(category.key)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${allSelected ? 'bg-green-100' : someSelected ? 'bg-yellow-100' : 'bg-gray-200'}`}>
                            <Shield className={`w-5 h-5 ${allSelected ? 'text-green-600' : someSelected ? 'text-yellow-600' : 'text-gray-400'}`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{category.name}</h3>
                            <p className="text-sm text-gray-600">
                              {selectedInCategory}/{categoryPerms.length} permissions selected
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {isExpanded && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSelectAllInCategory(category);
                              }}
                              className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              {allSelected ? 'Deselect All' : 'Select All'}
                            </button>
                          )}
                          <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Permissions in Category */}
                    {isExpanded && (
                      <div className="p-4 bg-white">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {categoryPerms.map(permission => {
                            const isSelected = selectedPermissions.includes(permission);
                            return (
                              <label
                                key={permission}
                                className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTogglePermission(permission)}
                                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <span className={`font-mono text-xs px-2 py-0.5 rounded ${
                                      isSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {permission}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1">{availablePermissions[permission]}</p>
                                </div>
                                {isSelected ? (
                                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0" />
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {!useRoleDefaults && (
                <span className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span>Custom permissions will override role defaults</span>
                </span>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    <span>Save Permissions</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal;
